import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Eye, Info, Link as LinkIcon } from "lucide-react";
import { Button } from "../../components/ui/Button";
import ViewWebsiteButton from "../../components/ui/ViewWebsiteButton";
import SectionHeader from "../../components/ui/SectionHeader";
import FormField from "../../components/ui/FormField";
import { useAuth } from "../../auth/AuthContext";
import { getWebsiteSettings, updateFooterSettings } from "../../services/websitesetting";
import { useSectionContext } from "../../context/SectionContext";
import { getNextSectionRoute } from "../../lib/getNextSectionRoute";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useToast } from "../../hooks/useToast";
import InfoContainer from "../../components/ui/InfoContainer";
import WebsiteSectionCard from "../../components/ui/WebsiteSectionCard";
import PageHeader from "../../components/ui/PageHeader";

type FooterDetails = {
  facebook: string;
  twitter: string;
  instagram: string;
  pinterest: string;
  linkedin: string;
  contactEmail: string;
  copyright: string;
};

const INITIAL_FOOTER_DETAILS: FooterDetails = {
  facebook: "",
  twitter: "",
  instagram: "",
  pinterest: "",
  linkedin: "",
  contactEmail: "",
  copyright: "",
};

const FooterSection: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { sections, completions, markSectionComplete } = useSectionContext();
  const toast = useToast();
  const [footerDetails, setFooterDetails] = useState(INITIAL_FOOTER_DETAILS);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { subdomain } = useOutletContext<{ subdomain: string }>();

  useEffect(() => {
    const fetchSettings = async () => {
      setIsInitialLoading(true);
      try {
        const data = await getWebsiteSettings();
        if (data?.footer_setting) {
          const { contact_email, footer_text, social_links } = data.footer_setting;

          const socialMap: Record<string, string> = {};
          if (Array.isArray(social_links)) {
            social_links.forEach((item: any) => {
              if (item.name && item.link) {
                socialMap[item.name.toLowerCase()] = item.link;
              }
            });
          }

          setFooterDetails(prev => ({
            ...prev,
            contactEmail: contact_email || "",
            copyright: footer_text || "",
            facebook: socialMap.facebook || "",
            twitter: socialMap.twitter || "",
            instagram: socialMap.instagram || "",
            pinterest: socialMap.pinterest || "",
            linkedin: socialMap.linkedin || "",
          }));
        }
      } catch (err: any) {
        console.error("fetchSettings ERROR:", err);
        console.error("fetchSettings Response:", err.response);
        console.error("fetchSettings Response Data:", err.response?.data);
        const status = err.response?.status;
        if (status === 404) {
          toast.error("Footer settings not found. Using defaults.");
        } else {
          toast.error("Failed to load footer settings.");
        }
      } finally {
        setIsInitialLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field: keyof FooterDetails, value: string) => {
    setFooterDetails((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const validateFooter = () => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (footerDetails.contactEmail) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(footerDetails.contactEmail)) {
        newErrors.contactEmail = "Please enter a valid email address";
      }
    }

    // URL validation helper
    const isValidUrl = (url: string) => {
      if (!url) return true; // Empty is okay
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    };

    // Validate social links
    if (footerDetails.facebook && !isValidUrl(footerDetails.facebook)) {
      newErrors.facebook = "Please enter a valid URL";
    }
    if (footerDetails.twitter && !isValidUrl(footerDetails.twitter)) {
      newErrors.twitter = "Please enter a valid URL";
    }
    if (footerDetails.instagram && !isValidUrl(footerDetails.instagram)) {
      newErrors.instagram = "Please enter a valid URL";
    }
    if (footerDetails.pinterest && !isValidUrl(footerDetails.pinterest)) {
      newErrors.pinterest = "Please enter a valid URL";
    }
    if (footerDetails.linkedin && !isValidUrl(footerDetails.linkedin)) {
      newErrors.linkedin = "Please enter a valid URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFooter()) {
      toast.error("Please fix the validation errors.");
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const payload = {
        contactEmail: footerDetails.contactEmail,
        // Copyright is hardcoded on public website templates (Powered by Glossa)
        copyright: footerDetails.copyright || "All rights reserved.",
        social_links: {
          facebook: footerDetails.facebook,
          twitter: footerDetails.twitter,
          instagram: footerDetails.instagram,
          pinterest: footerDetails.pinterest,
          linkedin: footerDetails.linkedin
        }
      };

      await updateFooterSettings(payload);
      markSectionComplete("footersection", true);

      // No navigation

      toast.success("Footer settings updated successfully!");

    } catch (err: any) {
      console.error("handleSave ERROR:", err);
      console.error("handleSave Response:", err.response);
      console.error("handleSave Response Data:", err.response?.data);

      if (err?.type === "serverValidation") {
        setErrors(err.errors);
        toast.error("Validation failed. Please check your links.");
      } else {
        const status = err.response?.status;
        let userMessage = "Failed to save footer settings.";

        if (status === 404) {
          userMessage = "Footer configuration not found. Please refresh the page.";
        } else if (status === 403) {
          userMessage = "You don't have permission to edit the footer.";
        } else if (status === 422) {
          userMessage = "Invalid data provided. Please check all fields.";
        } else if (status === 500) {
          userMessage = "Server error while saving footer. Please try again later.";
        } else if (err.message === "Network Error") {
          userMessage = "Connection lost. Please check your internet and try again.";
        }

        toast.error(userMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {(isInitialLoading || isLoading) && (
        <GlobalLoader
          initialPageLoad={isInitialLoading}
          fullScreen={isLoading}
          description={isLoading ? "Saving changes..." : "Loading footer settings..."}
        />
      )}
      <div className="space-y-5">
        {/* Header Section */}
        <PageHeader
          title="Footer Settings"
          description="Customize your website footer social links and contact email"
          variant="card"
          className="mb-5"
          mobileActionsInHeader={true}
          actions={
            <ViewWebsiteButton subdomain={subdomain} />
          }
        />

        {/* Main Content */}
        <WebsiteSectionCard
          title="Footer Content"
          icon={LinkIcon}
        >
          <div className="p-0 space-y-6">
            {/* Social Links Section */}
            <div>
              <SectionHeader title="Social Media Links" />

              <InfoContainer
                message="Add your social media profiles. Enter full URLs (e.g. https://facebook.com/page)."
                iconSize={13}
                className="mb-4"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField
                  label="Facebook"
                  name="facebook"
                  value={footerDetails.facebook}
                  onChange={(_, val) => handleChange("facebook", val)}
                  type="text"
                  placeholder="https://facebook.com/yourpage"
                  error={errors.facebook}
                />
                <FormField
                  label="Instagram"
                  name="instagram"
                  value={footerDetails.instagram}
                  onChange={(_, val) => handleChange("instagram", val)}
                  type="text"
                  placeholder="https://instagram.com/yourhandle"
                  error={errors.instagram}
                />
                <FormField
                  label="Twitter (X)"
                  name="twitter"
                  value={footerDetails.twitter}
                  onChange={(_, val) => handleChange("twitter", val)}
                  type="text"
                  placeholder="https://twitter.com/yourhandle"
                  error={errors.twitter}
                />
                <FormField
                  label="LinkedIn"
                  name="linkedin"
                  value={footerDetails.linkedin}
                  onChange={(_, val) => handleChange("linkedin", val)}
                  type="text"
                  placeholder="https://linkedin.com/in/yourprofile"
                  error={errors.linkedin}
                />
                <FormField
                  label="Pinterest"
                  name="pinterest"
                  value={footerDetails.pinterest}
                  onChange={(_, val) => handleChange("pinterest", val)}
                  type="text"
                  placeholder="https://pinterest.com/yourprofile"
                  error={errors.pinterest}
                />
              </div>
            </div>

            {/* Contact Section */}
            <div>
              <SectionHeader title="Contact" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField
                  label="Contact Email"
                  name="contactEmail"
                  value={footerDetails.contactEmail}
                  onChange={(_, val) => handleChange("contactEmail", val)}
                  type="email"
                  placeholder="contact@yourbusiness.com"
                  error={errors.contactEmail}
                />
              </div>
            </div>

            {/* Footer Actions */}
            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={isLoading}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-8 py-2.5 text-sm font-medium shadow-sm hover:shadow-md transition-all duration-200"
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </WebsiteSectionCard>
      </div>
    </>
  );
};

export default React.memo(FooterSection);
